import {RfcStatus} from '@common/types/rfc';
import {CheckCircle, Clock, FileText, RefreshCw, XCircle} from 'lucide-react';
import {Badge} from '@common-web/components/ui/badge';

/** Every non-archived workflow status, in lifecycle order — the standard set for status filters. */
export const RFC_WORKFLOW_STATUSES: RfcStatus[] = [
  RfcStatus.DRAFT,
  RfcStatus.SUBMITTED,
  RfcStatus.BROKER_REVIEW,
  RfcStatus.BROKER_APPROVED,
  RfcStatus.CLIENT_REVIEW,
  RfcStatus.CLIENT_APPROVED,
  RfcStatus.MSA_PENDING,
  RfcStatus.MSA_SENT,
  RfcStatus.MSA_SIGNED,
  RfcStatus.CBS_PROCESSING,
  RfcStatus.COMPLETED,
  RfcStatus.REJECTED,
  RfcStatus.ON_HOLD,
];

export const getStatusIcon = (status: RfcStatus) => {
  switch (status) {
    case RfcStatus.CLIENT_APPROVED:
    case RfcStatus.BROKER_APPROVED:
    case RfcStatus.MSA_SIGNED:
    case RfcStatus.COMPLETED:
      return <CheckCircle className='h-4 w-4 text-green-500' />;
    case RfcStatus.CLIENT_REVIEW:
    case RfcStatus.CBS_PROCESSING:
    case RfcStatus.MSA_SENT:
      return <RefreshCw className='h-4 w-4 text-blue-600' />;
    case RfcStatus.ARCHIVED:
    case RfcStatus.REJECTED:
      return <XCircle className='h-4 w-4 text-red-500' />;
    case RfcStatus.SUBMITTED:
    case RfcStatus.ON_HOLD:
    case RfcStatus.BROKER_REVIEW:
    case RfcStatus.MSA_PENDING:
      return <Clock className='h-4 w-4 text-orange-500' />;
    case RfcStatus.DRAFT:
    default:
      return <FileText className='h-4 w-4' />;
  }
};

export const getStatusColor = (status: RfcStatus) => {
  switch (status) {
    case RfcStatus.DRAFT:
      return 'secondary';
    case RfcStatus.SUBMITTED:
    case RfcStatus.BROKER_REVIEW:
    case RfcStatus.MSA_PENDING:
      return 'warning';
    case RfcStatus.CLIENT_REVIEW:
    case RfcStatus.CBS_PROCESSING:
    case RfcStatus.MSA_SENT:
      return 'info';
    case RfcStatus.CLIENT_APPROVED:
    case RfcStatus.BROKER_APPROVED:
    case RfcStatus.MSA_SIGNED:
    case RfcStatus.COMPLETED:
      return 'success';
    case RfcStatus.REJECTED:
    case RfcStatus.ARCHIVED:
      return 'destructive';
    case RfcStatus.ON_HOLD:
      return 'warning';
    default:
      return 'secondary';
  }
};

export function getCompanyName(rfc: {
  companyName?: string | null;
  section125FormData?: {groupInfo: {companyName?: string}} | null;
  mecFormData?: {groupInfo: {companyName?: string}} | null;
}) {
  return (
    rfc.companyName ||
    rfc.section125FormData?.groupInfo.companyName ||
    rfc.mecFormData?.groupInfo.companyName ||
    'No company name'
  );
}

/** Standard status cell: status icon + colored badge with the display name. */
export function RfcStatusBadge({status}: {status: RfcStatus}) {
  return (
    <div className='flex items-center gap-2 text-black'>
      {getStatusIcon(status)}
      <Badge variant={getStatusColor(status)}>{getRfcStatusDisplayName(status)}</Badge>
    </div>
  );
}

export function getRfcStatusDisplayName(status: RfcStatus) {
  switch (status) {
    case RfcStatus.DRAFT:
      return 'Draft';
    case RfcStatus.SUBMITTED:
      return 'Submitted';
    case RfcStatus.CLIENT_REVIEW:
      return 'In Review';
    case RfcStatus.BROKER_REVIEW:
      return 'Broker Review';
    case RfcStatus.BROKER_APPROVED:
      return 'Broker Approved';
    case RfcStatus.CLIENT_APPROVED:
      return 'Approved By Client';
    case RfcStatus.MSA_PENDING:
      return 'MSA Pending';
    case RfcStatus.MSA_SENT:
      return 'MSA Sent';
    case RfcStatus.MSA_SIGNED:
      return 'MSA Signed';
    case RfcStatus.CBS_PROCESSING:
      return 'Internal Processing';
    case RfcStatus.COMPLETED:
      return 'Completed';
    case RfcStatus.REJECTED:
      return 'Rejected';
    case RfcStatus.ARCHIVED:
      return 'Archived';
    case RfcStatus.ON_HOLD:
      return 'On Hold';
    default:
      return (status as string).replace('_', ' ');
  }
}
